// Rules for handling attachment files on the user's machine. Attachment names come from the sender, so they are
// untrusted: they can carry path separators, Unicode direction tricks ("invoice‮fdp.exe") or an executable extension.

/**
 * Extensions that run code (or hand control to something that does) when opened. Opening these from an email is the
 * classic malware path, so the desktop app never opens them directly; the user can still save them deliberately.
 */
export const RISKY_EXTENSIONS = new Set([
  // Windows executables, scripts, installers and shortcuts
  'exe', 'com', 'scr', 'pif', 'cpl', 'msi', 'msp', 'mst', 'msc', 'bat', 'cmd', 'ps1', 'psm1', 'psd1', 'ps1xml', 'psc1',
  'vb', 'vbs', 'vbe', 'js', 'jse', 'wsf', 'wsh', 'ws', 'wsc', 'hta', 'jar', 'lnk', 'url', 'scf', 'reg', 'inf', 'gadget',
  'application', 'appref-ms', 'appx', 'appxbundle', 'msix', 'msixbundle', 'dll', 'sys', 'ocx', 'library-ms',
  'settingcontent-ms', 'xll', 'xlam', 'ppam', 'iqy', 'slk', 'chm', 'hlp', 'mshxml', 'diagcab', 'theme', 'themepack',
  // disk images and archives that auto-mount or bypass Mark-of-the-Web on Windows
  'iso', 'img', 'vhd', 'vhdx',
  // macOS apps, installers, scripts and launchers
  'app', 'command', 'tool', 'terminal', 'pkg', 'mpkg', 'scpt', 'scptd', 'applescript', 'workflow', 'action',
  'webloc', 'inetloc', 'fileloc', 'prefpane', 'kext', 'dylib', 'dmg',
  // Unix-ish scripts and packages
  'sh', 'bash', 'zsh', 'csh', 'ksh', 'fish', 'run', 'bin', 'desktop', 'appimage', 'deb', 'rpm', 'py', 'pyw', 'pl', 'rb',
  'php', 'elf', 'out',
]);

/** Unicode format characters used to disguise names (RLO/LRO/RLE/LRE/PDF, isolates, zero-width marks). */
const BIDI_AND_INVISIBLE = /[​-‏‪-‮⁦-⁩﻿]/g;

/**
 * A file name that is safe to create on Windows, macOS and Linux: no directories, no reserved characters or device
 * names, no direction-override tricks, no trailing dots/spaces (Windows silently drops them, turning "a.exe." into
 * "a.exe"), and a bounded length that keeps the extension.
 */
export function safeAttachmentName(raw: string | undefined | null): string {
  let name = (raw ?? '').normalize('NFC').replace(BIDI_AND_INVISIBLE, '');
  name = name.split(/[\\/]/).pop() ?? '';
  name = name.replace(/[<>:"|?*\u0000-\u001F\u007F]/g, '_').replace(/[. ]+$/g, '').replace(/^[. ]+/g, '').trim();
  if (/^(con|prn|aux|nul|com\d|lpt\d)(\..*)?$/i.test(name)) name = `_${name}`;
  if (!name) name = 'attachment';
  if (name.length > 180) {
    const dot = name.lastIndexOf('.');
    const ext = dot > 0 && name.length - dot <= 16 ? name.slice(dot) : '';
    name = name.slice(0, 180 - ext.length) + ext;
  }
  return name;
}

/** Lower-case extension of an (already sanitized) name, without the dot; '' when there is none. */
export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * Whether opening this attachment could run code. Checks the sanitized name (so "x.exe." and "x‮exe.pdf" are caught)
 * and, as a backstop, executable MIME types whatever the name says.
 */
export function isRiskyAttachment(fileName: string, contentType?: string): boolean {
  if (RISKY_EXTENSIONS.has(extensionOf(safeAttachmentName(fileName)))) return true;
  const type = (contentType ?? '').toLowerCase().split(';')[0].trim();
  return /^application\/(x-(msdownload|msdos-program|ms-installer|msi|sh|bat|executable|mach-binary|elf|apple-diskimage|java-archive|ms-shortcut)|vnd\.microsoft\.portable-executable|hta|javascript|x-javascript|java-archive|vnd\.apple\.installer\+xml)$/.test(type);
}
